import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const BASE_URL = `${API_BASE_PATH}`;

// ---- Interfaces ----

export interface ShiftPayload {
  shift_name: string;
  shift_start_time: string; // ISO time format string
  shift_end_time: string;
  is_predefined: boolean;
}

export interface Shift {
  shift_name: string;
  shift_start_time: string;
  shift_end_time: string;
  duration_hours: number;
  is_predefined: boolean;
  created_at: string;
  updated_at: string | null;
  shift_type_id: number;
}

export interface AssignShiftPayload {
  employee_id?: string | null;
  shift_type_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  max_start_time?: string | null;
  max_end_time?: string | null;
  break_time?: string | null;
  is_active?: boolean | null;
}

export interface AssignedShift {
  employee_id: string;
  shift_type_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  max_start_time: string;
  max_end_time: string;
  break_time: string;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
  employee_shift_id: number;
  shift_type?: {
    shift_name: string;
    shift_start_time: string;
    shift_end_time: string;
    duration_hours: number;
    is_predefined: boolean;
    created_at: string;
    updated_at: string | null;
  };
}

// ---- Utility: Append payload values safely ----

const appendFormData = (
  formData: URLSearchParams,
  data: Record<string, any>
) => {
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value !== null ? String(value) : "");
  });
};

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ---- Create Shift ----

export const createShift = async (data: ShiftPayload, token: string | null) => {
  if (!token) throw new Error("Token is required");
  const formData = new URLSearchParams();
  appendFormData(formData, data);

  try {
    const response = await axios.post(
      `${BASE_URL}/shift/create`,
      formData.toString(),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    toast.success("Shift created successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ---- Get Shifts (Shift Types) ----

export const useGetShifts = (token?: string | null) => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchShifts = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${BASE_URL}/shift/get_all`, {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setShifts(data);
      return data;
    } catch (err: any) {
      const msg = extractErrorMessage(err);
      setError(msg);
      toast.error(msg);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [token]);

  return { shifts, loading, error, fetchShifts };
};

// ---- Assign Shift to Employee ----

export const assignShift = async (
  payload: AssignShiftPayload,
  token: string | null
) => {
  if (!token) throw new Error("Token is required");
  const formData = new URLSearchParams();

  const safeAppend = (key: string, value: string | number | boolean | null) => {
    if (value !== null) {
      formData.append(key, String(value));
    }
  };

  if (payload.employee_id !== undefined)
    safeAppend("employee_id", payload.employee_id);
  if (payload.shift_type_id !== undefined)
    safeAppend("shift_type_id", payload.shift_type_id);
  if (payload.start_date !== undefined)
    safeAppend("start_date", payload.start_date);
  if (payload.end_date !== undefined) safeAppend("end_date", payload.end_date);
  if (payload.start_time !== undefined)
    safeAppend("start_time", payload.start_time);
  if (payload.end_time !== undefined) safeAppend("end_time", payload.end_time);
  if (payload.max_start_time !== undefined)
    safeAppend("max_start_time", payload.max_start_time);
  if (payload.max_end_time !== undefined)
    safeAppend("max_end_time", payload.max_end_time);
  if (payload.break_time !== undefined)
    safeAppend("break_time", payload.break_time);
  if (payload.is_active !== undefined)
    safeAppend("is_active", payload.is_active);

  try {
    const response = await axios.post(`${BASE_URL}/shift_assign/`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Authorization: "Bearer " + token,
      },
    });
    toast.success("Shift assigned successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ---- Get All Assigned Shifts ----

export const useGetAssignedShifts = (token: string | null) => {
  const [assignedShifts, setAssignedShifts] = useState<AssignedShift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignedShifts = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/shift_assign/all`, {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      });
      setAssignedShifts(res.data);
    } catch (err: any) {
      const msg = extractErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignedShifts();
  }, [token]);

  return { assignedShifts, loading, error, fetchAssignedShifts };
};

// ---- Update Assigned Shift ----

export const updateAssignedShift = async (
  id: number,
  payload: AssignShiftPayload,
  token: string | null
) => {
  if (!token) throw new Error("Token is required");
  const formData = new URLSearchParams();

  const safeAppend = (key: string, value: string | number | boolean | null) => {
    if (value !== null && value !== undefined) {
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
      } else {
        formData.append(key, String(value));
      }
    }
  };

  if (payload.employee_id !== undefined)
    safeAppend("employee_id", payload.employee_id);
  if (payload.shift_type_id !== undefined)
    safeAppend("shift_type_id", payload.shift_type_id);
  if (payload.start_date !== undefined)
    safeAppend("start_date", payload.start_date);
  if (payload.end_date !== undefined) safeAppend("end_date", payload.end_date);
  if (payload.start_time !== undefined)
    safeAppend("start_time", payload.start_time);
  if (payload.end_time !== undefined) safeAppend("end_time", payload.end_time);
  if (payload.max_start_time !== undefined)
    safeAppend("max_start_time", payload.max_start_time);
  if (payload.max_end_time !== undefined)
    safeAppend("max_end_time", payload.max_end_time);
  if (payload.break_time !== undefined)
    safeAppend("break_time", payload.break_time);
  if (payload.is_active !== undefined)
    safeAppend("is_active", payload.is_active);

  try {
    const response = await axios.put(
      `${BASE_URL}/shift_assign/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );
    toast.success("Shift updated successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ---- Delete Shift ----

// export const deleteShift = async (shiftId: number, token: string | null) => {
//   if (!token) throw new Error("Token is required");
//   try {
//     const response = await axios.delete(`${BASE_URL}/shift/${shiftId}`, {
//       headers: { Accept: "application/json", Authorization: "Bearer " + token },
//     });
//     toast.success("Shift deleted successfully");
//     return response.data;
//   } catch (error: any) {
//     const msg = extractErrorMessage(error);
//     toast.error(msg);
//     throw error;
//   }
// };

export const deleteShift = async function deleteShift(shiftId: number,  token: string | null): Promise<void> {
  try {
    const response = await axios.delete(`${BASE_URL}/shift_assign/${shiftId}`, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Authorization: "Bearer " + token,
      },
    });
    toast.success("Shift deleted successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
}